import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Phone, Building2, Calendar, Trash2, User, FileText, Download, Headphones, Image, FileDown, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { mapDatabaseError } from "@/lib/errorUtils";

type TalentRequest = {
  id: string;
  name: string;
  email: string;
  company: string | null;
  phone: string | null;
  message: string;
  talent_id: string | null;
  status: string;
  request_type: string;
  created_at: string;
  talent?: {
    name: string;
    slug: string;
  } | null;
};

const statusColors: Record<string, string> = {
  new: "bg-primary text-primary-foreground",
  read: "bg-blue-100 text-blue-800",
  replied: "bg-green-100 text-green-800",
  archived: "bg-muted text-muted-foreground",
};

const requestTypeColors: Record<string, string> = {
  contact: "bg-slate-100 text-slate-800",
  application: "bg-purple-100 text-purple-800",
};

const getRequestTypeLabel = (request: TalentRequest) => {
  if (request.request_type === "application") return "Talent Application";
  if (request.talent_id) return "Talent Request";
  return "General Inquiry";
};

const extractStoragePath = (url: string): string | null => {
  const idx = url.indexOf("/talent_applications/");
  if (idx === -1) return null;
  return url.substring(idx + "/talent_applications/".length);
};

const parseApplicationMessage = (message: string) => {
  const lines = message.split("\n");
  const files: { label: string; url: string; icon: "audio" | "image" | "resume" | "link" }[] = [];
  const textLines: string[] = [];
  let inExperience = false;
  const experienceLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed === "[TALENT APPLICATION]" || trimmed === "") continue;

    const audioMatch = trimmed.match(/^Uploaded Audio Demo:\s*(.+)/);
    const headMatch = trimmed.match(/^Headshot:\s*(.+)/);
    const resumeMatch = trimmed.match(/^Resume:\s*(.+)/);
    const demoLink = trimmed.match(/^Demo Reel Link:\s*(.+)/);

    if (audioMatch) {
      files.push({ label: "Audio Demo", url: audioMatch[1].trim(), icon: "audio" });
    } else if (headMatch) {
      files.push({ label: "Headshot", url: headMatch[1].trim(), icon: "image" });
    } else if (resumeMatch) {
      files.push({ label: "Resume", url: resumeMatch[1].trim(), icon: "resume" });
    } else if (demoLink) {
      files.push({ label: "Demo Reel", url: demoLink[1].trim(), icon: "link" });
    } else if (trimmed === "Experience & Background:") {
      inExperience = true;
    } else if (inExperience) {
      experienceLines.push(trimmed);
    } else {
      textLines.push(trimmed);
    }
  }

  return { files, textLines, experienceLines };
};

// Component that generates a signed URL on click for private bucket files
const SignedFileLink = ({ file }: { file: { label: string; url: string; icon: "audio" | "image" | "resume" | "link" } }) => {
  const IconComp = file.icon === "audio" ? Headphones : file.icon === "image" ? Image : file.icon === "resume" ? FileDown : LinkIcon;
  const isExternal = file.icon === "link";

  const handleClick = async (e: React.MouseEvent) => {
    if (isExternal) return;

    e.preventDefault();
    const storagePath = extractStoragePath(file.url);
    if (!storagePath) {
      toast.error("Could not resolve file path");
      return;
    }

    const { data, error } = await supabase.storage
      .from("talent_applications")
      .createSignedUrl(storagePath, 3600);

    if (error || !data?.signedUrl) {
      toast.error("Failed to generate download link");
      return;
    }

    window.open(data.signedUrl, "_blank");
  };

  return (
    <a
      href={isExternal ? file.url : "#"}
      onClick={handleClick}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors group cursor-pointer"
    >
      <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
        <IconComp size={18} className="text-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{file.label}</p>
        <p className="text-xs text-muted-foreground">Click to {isExternal ? "open" : "download"}</p>
      </div>
      <Download size={14} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </a>
  );
};

const InboxAdmin = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [talentFilter, setTalentFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [selectedRequest, setSelectedRequest] = useState<TalentRequest | null>(null);

  // Fetch talent list for filter dropdown
  const { data: talentList } = useQuery({
    queryKey: ["admin-talent-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("talent")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return data;
    },
  });

  const { data: requests, isLoading } = useQuery({
    queryKey: ["admin-requests", statusFilter, talentFilter, typeFilter],
    queryFn: async () => {
      let query = supabase
        .from("talent_requests")
        .select("*, talent:talent_id(name, slug)")
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      if (talentFilter !== "all") {
        if (talentFilter === "none") {
          query = query.is("talent_id", null);
        } else {
          query = query.eq("talent_id", talentFilter);
        }
      }

      if (typeFilter !== "all") {
        query = query.eq("request_type", typeFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as TalentRequest[];
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("talent_requests")
        .update({ status })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-requests"] });
      queryClient.invalidateQueries({ queryKey: ["new-requests-count"] });
      toast.success("Status updated");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("talent_requests")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-requests"] });
      queryClient.invalidateQueries({ queryKey: ["new-requests-count"] });
      setSelectedRequest(null);
      toast.success("Request deleted");
    },
    onError: (error) => {
      toast.error(mapDatabaseError(error));
    },
  });

  const handleViewRequest = (request: TalentRequest) => {
    setSelectedRequest(request);
    if (request.status === "new") {
      updateStatusMutation.mutate({ id: request.id, status: "read" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold">Inbox</h1>
          <p className="text-muted-foreground mt-1">
            View and manage talent requests from the contact form.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-44 bg-background">
              <FileText size={16} className="mr-2 text-muted-foreground" />
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent className="bg-popover z-50">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="application">Talent Applications</SelectItem>
              <SelectItem value="contact">Contact (Talent Requests & General)</SelectItem>
            </SelectContent>
          </Select>
          <Select value={talentFilter} onValueChange={setTalentFilter}>
            <SelectTrigger className="w-48 bg-background">
              <User size={16} className="mr-2 text-muted-foreground" />
              <SelectValue placeholder="Filter by talent" />
            </SelectTrigger>
            <SelectContent className="bg-popover z-50">
              <SelectItem value="all">All Talents</SelectItem>
              <SelectItem value="none">No Talent Selected</SelectItem>
              {talentList?.map((talent) => (
                <SelectItem key={talent.id} value={talent.id}>
                  {talent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover z-50">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="read">Read</SelectItem>
              <SelectItem value="replied">Replied</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : requests && requests.length > 0 ? (
        <div className="space-y-3">
          {requests.map((request) => (
            <Card
              key={request.id}
              className={`cursor-pointer transition-shadow hover:shadow-md ${
                request.status === "new" ? "border-primary/50" : ""
              }`}
              onClick={() => handleViewRequest(request)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-medium">{request.name}</h3>
                      <Badge className={requestTypeColors[request.request_type] || requestTypeColors.contact}>
                        {getRequestTypeLabel(request)}
                      </Badge>
                      <Badge className={statusColors[request.status]}>
                        {request.status}
                      </Badge>
                      {request.talent && (
                        <Badge variant="outline" className="gap-1">
                          <User size={12} />
                          {request.talent.name}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{request.email}</p>
                    <p className="text-sm mt-2 line-clamp-2 text-muted-foreground">
                      {request.request_type === "application"
                        ? parseApplicationMessage(request.message).experienceLines.join(" ") || "Talent application submitted"
                        : request.message}
                    </p>
                  </div>
                  <div className="text-right text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      {new Date(request.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No requests found.</p>
          </CardContent>
        </Card>
      )}

      {/* Request Detail Dialog */}
      <Dialog
        open={!!selectedRequest}
        onOpenChange={(open) => !open && setSelectedRequest(null)}
      >
        {selectedRequest && (
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Request from {selectedRequest.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Talent Requested */}
              {selectedRequest.talent && (
                <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                    <User size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Talent Requested</p>
                    <p className="font-medium">{selectedRequest.talent.name}</p>
                  </div>
                </div>
              )}

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <Mail size={16} className="text-muted-foreground shrink-0" />
                  <a
                    href={`mailto:${selectedRequest.email}`}
                    className="text-primary hover:underline"
                  >
                    {selectedRequest.email}
                  </a>
                </div>
                {selectedRequest.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone size={16} className="text-muted-foreground shrink-0" />
                    <a
                      href={`tel:${selectedRequest.phone}`}
                      className="text-primary hover:underline"
                    >
                      {selectedRequest.phone}
                    </a>
                  </div>
                )}
                {selectedRequest.company && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 size={16} className="text-muted-foreground" />
                    {selectedRequest.company}
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar size={16} className="text-muted-foreground" />
                  {new Date(selectedRequest.created_at).toLocaleString()}
                </div>
              </div>

              {/* Message */}
              {selectedRequest.request_type === "application" ? (
                (() => {
                  const { files, textLines, experienceLines } = parseApplicationMessage(selectedRequest.message);
                  return (
                    <div className="space-y-4">
                      {files.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium mb-2">Attachments</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {files.map((file, i) => (
                              <SignedFileLink key={i} file={file} />
                            ))}
                          </div>
                        </div>
                      )}
                      {experienceLines.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium mb-2">Experience & Background</h4>
                          <div className="bg-muted p-4 rounded-lg whitespace-pre-wrap">
                            {experienceLines.join("\n")}
                          </div>
                        </div>
                      )}
                      {textLines.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium mb-2">Additional Notes</h4>
                          <div className="bg-muted p-4 rounded-lg whitespace-pre-wrap">
                            {textLines.join("\n")}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div>
                  <h4 className="text-sm font-medium mb-2">Message</h4>
                  <div className="bg-muted p-4 rounded-lg whitespace-pre-wrap">
                    {selectedRequest.message}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <Select
                    value={selectedRequest.status}
                    onValueChange={(status) =>
                      updateStatusMutation.mutate({
                        id: selectedRequest.id,
                        status,
                      })
                    }
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="read">Read</SelectItem>
                      <SelectItem value="replied">Replied</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      if (confirm("Delete this request?")) {
                        deleteMutation.mutate(selectedRequest.id);
                      }
                    }}
                  >
                    <Trash2 size={16} className="mr-2" />
                    Delete
                  </Button>
                  <Button asChild>
                    <a href={`mailto:${selectedRequest.email}`}>
                      <Mail size={16} className="mr-2" />
                      Reply via Email
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};

export default InboxAdmin;
